import type { TFunction } from "i18next";
import moment, { type Moment } from "moment";

export type TimeLeft =
  | { unit: "passed" }
  | { unit: "minutes"; minutes: number }
  | { unit: "hoursAndMinutes"; hours: number; minutes: number }
  | { unit: "daysAndHours"; days: number; hours: number };

const MINUTES_PER_HOUR = 60;
const MINUTES_PER_DAY = 24 * MINUTES_PER_HOUR;

export const timeLeftUntil = (deadline: string, now?: Moment): TimeLeft => {
  const minutesLeft = moment(deadline).diff(now ?? moment(), "minutes");
  if (minutesLeft <= 0) return { unit: "passed" };
  if (minutesLeft < MINUTES_PER_HOUR) return { unit: "minutes", minutes: minutesLeft };
  if (minutesLeft < MINUTES_PER_DAY) {
    return {
      unit: "hoursAndMinutes",
      hours: Math.floor(minutesLeft / MINUTES_PER_HOUR),
      minutes: minutesLeft % MINUTES_PER_HOUR,
    };
  }
  return {
    unit: "daysAndHours",
    days: Math.floor(minutesLeft / MINUTES_PER_DAY),
    hours: Math.floor((minutesLeft % MINUTES_PER_DAY) / MINUTES_PER_HOUR),
  };
};

export const formatTimeLeft = (timeLeft: TimeLeft, t: TFunction): string => {
  switch (timeLeft.unit) {
    case "passed":
      return t("Deadline passed");
    case "minutes":
      return t("{{minutes}} min left", { minutes: timeLeft.minutes });
    case "hoursAndMinutes":
      return t("{{hours}} h {{minutes}} min left", { hours: timeLeft.hours, minutes: timeLeft.minutes });
    case "daysAndHours":
      return t("{{days}} d {{hours}} h left", { days: timeLeft.days, hours: timeLeft.hours });
  }
};
